import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { EnvelopeSimple, LockKey, CheckCircle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDetail = (d) => {
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || '').join(' ');
  return 'Une erreur est survenue. Réessayez.';
};

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#1a1214] flex items-center justify-center p-4">
    <div className="w-full max-w-md bg-white/10 backdrop-blur-2xl rounded-2xl border border-white/20 shadow-2xl p-8">
      <h1 className="text-white text-xl font-bold mb-1">MAGERE Viandes</h1>
      {children}
      <p className="mt-6 text-center">
        <Link to="/login" className="text-white/60 text-[13px] hover:text-white underline" data-testid="back-to-login">← Retour à la connexion</Link>
      </p>
    </div>
  </div>
);

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Shell>
      <p className="text-white/60 text-[13px] mb-6">Mot de passe oublié</p>
      {sent ? (
        <div className="text-center py-4" data-testid="forgot-success">
          <CheckCircle size={44} className="text-emerald-400 mx-auto mb-3" weight="fill" />
          <p className="text-white text-[14px]">Si un compte existe avec <b>{email}</b>, un email avec un lien de réinitialisation vient d'être envoyé.</p>
          <p className="text-white/50 text-[12px] mt-2">Pensez à vérifier vos spams. Le lien est valable 1 heure.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && <div className="p-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-200 text-[13px]" data-testid="forgot-error">{error}</div>}
          <div>
            <Label className="text-white/80 text-[13px]">Votre email</Label>
            <div className="relative mt-1.5">
              <EnvelopeSimple size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                placeholder="votre@email.fr" className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/30"
                data-testid="forgot-email-input" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#c2185b] hover:bg-[#a01549] text-white font-semibold" data-testid="forgot-submit">
            {loading ? 'Envoi…' : 'Envoyer le lien de réinitialisation'}
          </Button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Les mots de passe ne correspondent pas'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Shell>
      <p className="text-white/60 text-[13px] mb-6">Choisissez un nouveau mot de passe</p>
      {done ? (
        <div className="text-center py-4" data-testid="reset-success">
          <CheckCircle size={44} className="text-emerald-400 mx-auto mb-3" weight="fill" />
          <p className="text-white text-[14px]">Mot de passe mis à jour ! Redirection vers la connexion…</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && <div className="p-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-200 text-[13px]" data-testid="reset-error">{error}</div>}
          <div>
            <Label className="text-white/80 text-[13px]">Nouveau mot de passe</Label>
            <div className="relative mt-1.5">
              <LockKey size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input type="password" required minLength={6} value={password} onChange={e => setPassword(e.target.value)}
                placeholder="6 caractères minimum" className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/30"
                data-testid="reset-password-input" />
            </div>
          </div>
          <div>
            <Label className="text-white/80 text-[13px]">Confirmez le mot de passe</Label>
            <Input type="password" required value={confirm} onChange={e => setConfirm(e.target.value)}
              className="mt-1.5 bg-white/10 border-white/20 text-white" data-testid="reset-confirm-input" />
          </div>
          <Button type="submit" disabled={loading || !token} className="w-full rounded-full bg-[#c2185b] hover:bg-[#a01549] text-white font-semibold" data-testid="reset-submit">
            {loading ? 'Enregistrement…' : 'Mettre à jour mon mot de passe'}
          </Button>
          {!token && <p className="text-amber-300 text-[12px] text-center">Lien invalide : ouvrez le lien reçu par email.</p>}
        </form>
      )}
    </Shell>
  );
}
